/* eslint-disable no-param-reassign */
import {
  Checkbox, InputNumber, Table,
} from 'antd';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { EditOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import '../tableTab/TableTab.css';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmModal from '../../../../../components/confirmModal/ConfirmModal';
import allActions from '../../../../../actions';
import { showNotification } from '../../../../../actions/utilities/Notifications/NotificationActions';

const consHistReviewcols = {
  matID: 'MATERIAL_MOVEMENT_DOCUMENT_SAP_ID',
  itemID: 'MATERIAL_MOVEMENT_DOCUMENT_ITEM_SAP_ID',
  docYr: 'EDAM_MATERIAL_DOCUMENT_YR',
  outlrOld: 'EXCLUDE_INCLUDE_OUTLIER_OLD',
  outlrNew: 'EXCLUDE_INCLUDE_OUTLIER',
  qtyOld: 'MATERIAL_BASE_UOM_QTY',
  qtyNew: 'ADJUST_QUANTITY',
};

const {
  matID, itemID, outlrOld, outlrNew, qtyOld, qtyNew, docYr,
} = consHistReviewcols;

function AdjustQuantity({
  value,
  record,
  updateTableData,
  enableSave,
  setEnableSave,
}) {
  const [edit, setEdit] = useState(false);
  const [qty, setQty] = useState(0);

  useEffect(() => {
    setQty(Number.parseFloat(value ?? 0, 10));
  }, [value]);

  const updateAtTableLevel = (e) => updateTableData({
    ...record,
    [qtyNew]: Number.parseFloat(e?.target?.value, 10),
  }, qtyNew);

  const onChangeQty = (v) => {
    if (!enableSave) setEnableSave(true);
    setQty(v);
  };

  return (
    <div className="inlinecell">
      {
        value >= 0 && value !== record?.MATERIAL_BASE_UOM_QTY && !edit
          ? <div style={{ display: 'inline' }}>{value}</div> : null
      }
      {
        edit ? (
          <InputNumber
            min={0}
            defaultValue={value}
            value={qty}
            size="small"
            onChange={(v) => onChangeQty(v)}
            onBlur={updateAtTableLevel}
          />
        ) : <EditOutlined onClick={() => setEdit(true)} />
      }
    </div>
  );
}

AdjustQuantity.propTypes = {
  value: PropTypes.number,
  record: PropTypes.objectOf(PropTypes.any).isRequired,
  updateTableData: PropTypes.func.isRequired,
  enableSave: PropTypes.bool,
  setEnableSave: PropTypes.func.isRequired,
};

AdjustQuantity.defaultProps = {
  value: 0,
  enableSave: true,
};

function ConsTableExclude({
  record, updateTableData, isExcluded,
}) {
  const [check, setCheck] = useState(false);

  useEffect(() => {
    setCheck(isExcluded);
  }, [isExcluded]);

  const updateAtTableLevel = (e) => {
    setCheck(e?.target?.checked);
    updateTableData({
      ...record,
      [outlrOld]: isExcluded,
      [outlrNew]: e?.target?.checked,
    }, outlrNew);
  };

  return (
    <Checkbox
      checked={check}
      onChange={(e) => updateAtTableLevel(e)}
    />
  );
}

ConsTableExclude.propTypes = {
  record: PropTypes.objectOf(PropTypes.any),
  updateTableData: PropTypes.func.isRequired,
  isExcluded: PropTypes.bool,
  // updatedData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
};
ConsTableExclude.defaultProps = {
  record: {},
  isExcluded: false,
  // updatedData: [],
};

function ConsTableReview({ data, visible, setVisible }) {
  const { plant, material } = useParams();
  const user = useSelector((state) => state.authState?.user);
  const dispatch = useDispatch();

  const reformatData = (changeLog) => changeLog.map((x) => ({
    changeLogID: uuidv4(),
    [matID]: x[matID],
    [itemID]: x[itemID],
    [outlrOld]: x[outlrOld],
    [outlrNew]: x[outlrNew],
    [qtyOld]: x[qtyOld],
    [qtyNew]: x[qtyNew],
    [docYr]: x[docYr],
  }));

  const onConfirm = () => {
    setVisible(false);
    if (!user?.uniqueId || !user?.account?.username) {
      dispatch(showNotification(
        {
          Type: 'error',
          Title: 'Consumption history',
          Message: 'Error occurred while updating consumption history parameters',
        },
      ));
      return;
    }
    dispatch(allActions.MaterialDetailsActions.updateConsHistory(
      plant,
      material,
      user?.uniqueId,
      user?.account?.username,
      reformatData(data),
    ));
  };

  const getColumn = (title, dataIndex, render = null) => {
    const col = { key: dataIndex, title, dataIndex };
    if (render) {
      col.render = render;
    }
    return col;
  };

  const exclOutlierRender = (val) => (val ? 'Exclude' : 'Include');

  const reviewTable = (
    <Table
      columns={[
        getColumn('Material Document ID', matID),
        getColumn('Material Doc Item ID', itemID),
        getColumn('Outlier old value', outlrOld, exclOutlierRender),
        getColumn('Outlier new value', outlrNew, exclOutlierRender),
        getColumn('Quantity old value', qtyOld),
        getColumn('Quantity new value', qtyNew),
      ]}
      dataSource={data}
    />
  );

  return (
    <ConfirmModal
      title="Consumption data changes"
      visible={visible}
      onOK={onConfirm}
      onCancel={() => setVisible(false)}
      message={reviewTable}
    />
  );
}

ConsTableReview.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  visible: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
};

ConsTableReview.defaultProps = {
  visible: false,
};

export {
  AdjustQuantity,
  ConsTableExclude,
  ConsTableReview,
  consHistReviewcols,
};
